import moment from "moment";
import "moment-timezone";
import { CONFIG } from "../config";

const guessedTimezone = moment.tz.guess();

/**
 * Formats a date using moment with user preferences
 */
export function formatDate(date, format = null) {
  // Guard against null/undefined early
  if (date == null) {
    return "N/A";
  }

  // date could be using moment, make sure if its a moment object we convert it to string
  if (moment.isMoment(date)) {
    date = date.toISOString();
  } else if (date instanceof Date) {
    // Normalize native Date instances
    date = date.toISOString();
  } else if (typeof date === "number") {
    // Normalize numeric timestamps
    const m = moment(date);
    if (!m.isValid()) {
      return "N/A";
    }
    date = m.toISOString();
  } else if (typeof date === "string") {
    // Trim and ensure non-empty string
    date = date.trim();
    if (!date.length) {
      return "N/A";
    }
  } else {
    // Unsupported type
    return "N/A";
  }

  //check if local storage has a date_format, if not use the default form CONFIG.defaultDateFormat
  const storedDateFormat = localStorage.getItem("date_format");
  let dateFormat =
    storedDateFormat !== null ? storedDateFormat : CONFIG.defaultDateFormat;

  const storedTimeFormat = localStorage.getItem("time_format");
  let timeFormat = storedTimeFormat !== null ? storedTimeFormat : "HH:mm";

  dateFormat = dateFormat + " " + timeFormat;

  //if format is passed, use that
  if (format) {
    dateFormat = format;
  }

  //timezone adjustment
  let tzFormat = localStorage.getItem("timezone");
  if (!tzFormat) {
    tzFormat = guessedTimezone;
  }

  //check date is valid
  if (!moment(date).isValid()) {
    return "N/A";
  }

  // Explicitly parse as UTC first, then convert to target timezone
  // This fixes the issue where dates without timezone indicators are assumed to be local time
  return moment.utc(date).tz(tzFormat).format(dateFormat);
}

export function formatDateOnly(date, format = null) {
  if (!date) return "N/A";

  const dateFormat =
    format || localStorage.getItem("date_format") || CONFIG.defaultDateFormat;
  const tzFormat = localStorage.getItem("timezone") || guessedTimezone;

  const m = moment.utc(date);
  if (!m.isValid()) {
    return "N/A";
  }

  return m.tz(tzFormat).format(dateFormat);
}

/**
 * Formats time only from a date
 */
export function formatTimeOnly(date) {
  if (!date) return "N/A";

  const timeFormat = localStorage.getItem("time_format") || "HH:mm";
  const tzFormat = localStorage.getItem("timezone") || guessedTimezone;

  return moment.utc(date).tz(tzFormat).format(timeFormat);
}

/**
 * Formats a Unix timestamp
 */
export function formatUnixDate(unixDate, format = null) {
  if (!unixDate) return "N/A";

  // Ensure unixDate is a valid number
  const numericUnixDate = parseFloat(unixDate);
  if (isNaN(numericUnixDate)) {
    return "N/A";
  }

  // Convert unix timestamp to date
  const date = moment.unix(numericUnixDate);

  // Validate the resulting moment object
  if (!date.isValid()) {
    return "N/A";
  }

  return formatDate(date, format);
}

/**
 * Formats speed with appropriate units
 */
export function formatSpeed(speed, addUnit = true, unitOnly = false) {
  if (speed === null || speed === undefined) {
    return "N/A";
  }

  // Ensure speed is a valid number
  const numericSpeed = parseFloat(speed);
  if (isNaN(numericSpeed)) {
    return "N/A";
  }

  // Get speed unit from localStorage with proper default
  let speedUnit = localStorage.getItem("speed_unit") ?? "mph";

  if (unitOnly) {
    return speedUnit;
  }

  let formattedSpeed;

  // Convert speed based on unit
  if (speedUnit === "mph") {
    formattedSpeed = (numericSpeed * 0.621371).toFixed(1);
  } else if (speedUnit === "ms") {
    formattedSpeed = (numericSpeed / 3.6).toFixed(2);
  } else {
    // Default is kmph
    formattedSpeed = numericSpeed.toFixed(1);
  }

  // Remove .0 for whole numbers (except for m/s which uses 2 decimal places)
  if (speedUnit !== "ms" && formattedSpeed.endsWith(".0")) {
    formattedSpeed = formattedSpeed.slice(0, -2);
  }

  if (!addUnit) {
    return parseFloat(formattedSpeed);
  }

  return `${formattedSpeed} ${speedUnit}`;
}

/**
 * Formats temperature with appropriate units
 */
export function formatTemperature(
  temperature,
  addUnit = false,
  onlyUnit = false
) {
  if (temperature === null || temperature === undefined) {
    return "N/A";
  }

  // Ensure temperature is a valid number
  const numericTemp = parseFloat(temperature);
  if (isNaN(numericTemp)) {
    return "N/A";
  }

  let tempUnit = localStorage.getItem("temperature_unit") ?? "C";

  if (onlyUnit) {
    return `°${tempUnit}`;
  }

  let formattedTemp;

  // Convert temperature based on unit
  if (tempUnit === "F") {
    formattedTemp = (numericTemp * 9) / 5 + 32;
  } else {
    // Default is Celsius
    formattedTemp = numericTemp;
  }

  // Format to 1 decimal place, then remove .0 for whole numbers
  let displayTemp = formattedTemp.toFixed(1);
  if (displayTemp.endsWith(".0")) {
    displayTemp = displayTemp.slice(0, -2);
  }

  if (!addUnit) {
    return parseFloat(displayTemp);
  }

  return `${displayTemp}°${tempUnit}`;
}
